import React, { FC, useEffect, useRef, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Button } from '@material-ui/core'
import Headline from 'uiKit/texts/Headline'
import NoChartData from 'uiKit/NoChartData'
import Search from 'uiKit/Search/Search'
import { filterBarSearchStyle } from 'uiKit/Search/styles'
import PageHeader from 'uiKit/PageHeader'
import FilterBar from 'uiKit/FilterBar'
import EmptyState from 'uiKit/EmptyState'
import { BotUsageProvider } from 'contexts/BotUsageContext'
import { isQcsBot } from 'constants/qcs'
import LoaderScreen from 'uiKit/loaders/loaderScreen'
import LoaderOverlay from 'uiKit/loaders/loaderOverlay'
import Pagination from 'uiKit/table/Pagination'
import { DEFAULT_DEBOUNCE_TIME } from 'constants/time'
import { useDebounce } from 'hooks/useDebounce'
import { parseRowsPerPageSelection } from 'constants/pagination'
import { ServiceUserType } from 'models/ServiceUsersTypes'
import { AddNewServiceUserModal } from './components/AddNewServiceUserModal/AddNewServiceUserModal'
import { deleteUsers, getAllCareProfessionals, exportConversations, getServiceUsers } from './api/serviceUsersApi'
import { ServiceUsersTable } from './components/ServiceUsersTable/ServiceUsersTable'
import {
  clearSelectedServiceUsersId,
  setIsLoadingServiceUsers,
  updateCurrentPage,
  updateRowsPerPage,
} from './actions/serviceUsersActions'
import {
  inputSearchStyles,
  buttonStylesCareBrain,
  buttonStylesQcs,
} from '../professionals/Professionals'
import moment from 'moment'

import {
  ServiceUsersButtonWrap,
  ServiceUsersContainer,
  ServiceUsersControlLine,
  ServiceUsersHeadlineWrap,
  ServiceUsersSearchWrap,
  ButtonTitle,
  ButtonTitleQcs,
} from './ServiceUsers.styles'
import { DeleteServiceUserModal } from './components/DeleteServiceUserModal/DeleteServiceUserModal'
import DuplicateAttentionModal from './components/DuplicateAttentionModal/DuplicateAttentionModal'

interface Props {
  serviceUsers: ServiceUserType[]
  currentPage: number
  totalElements: number
  match: any
  isLoadingServiceUsers: boolean
  selectedServiceUsersId: number[]
  rowsPerPage: number | string
}

const ServiceUsers: FC<Props> = ({
  serviceUsers,
  currentPage,
  totalElements,
  match,
  isLoadingServiceUsers,
  selectedServiceUsersId,
  rowsPerPage
}) => {
  const { params: { botId } } = match
  const activeBot = useSelector((state: any) => state.activeBot)
  const isQcs = isQcsBot(activeBot || botId)
  const [searchingValue, setSearchingValue] = useState('')
  const [isAddNewServiceUserModalOpen, setIsAddNewServiceUserModalOpen] = useState(false)
  const [duplicateAttentionModal, setDuplicateAttentionModal] = useState({
    isOpen: false,
    duplicateUsers: ''
  })
  const [deleteUserModal, setDeleteUserModal] = useState({
    isOpen: false,
    isSingleDelete: false,
    selectedUser: null
  })
  const [isCarePlanUploading, setIsCarePlanUploading] = useState(false)

  const debouncedSearch = useDebounce(searchingValue, DEFAULT_DEBOUNCE_TIME)
  const requestRef = useRef<AbortController | null>(null)
  const seqRef = useRef(0)

  useEffect(() => {
    requestRef.current?.abort()
    const controller = new AbortController()
    requestRef.current = controller
    const seq = ++seqRef.current

    setIsLoadingServiceUsers(true)

    // QCS has no Care Professionals UI — skip that list fetch.
    const requests = [
      getServiceUsers(botId, currentPage, debouncedSearch, rowsPerPage, controller.signal),
      ...(isQcs ? [] : [getAllCareProfessionals(botId)]),
    ]

    Promise.all(requests)
      .then(() => {
        if (seq !== seqRef.current) return
        setIsLoadingServiceUsers(false)
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
        if (seq !== seqRef.current) return
        setIsLoadingServiceUsers(false)
      })

    return () => controller.abort()
  }, [botId, currentPage, debouncedSearch, rowsPerPage, isQcs])

  useEffect(() => {
    return () => {
      updateCurrentPage(0)
      updateRowsPerPage(10)
    }
  }, [])

  const onChangeHandler = (e) => {
    setSearchingValue(e.target.value)
    updateCurrentPage(0)
  }

  const onChangePageHandler = (e, page) => {
    updateCurrentPage(page)
  }

  const onChangeRowsPerPageHandler = (event) => {
    updateRowsPerPage(parseRowsPerPageSelection(event.target.value))
    updateCurrentPage(0)
  }

  const onCloseDeleteModalHandler = () => {
    setDeleteUserModal({
      isOpen: false,
      isSingleDelete: false,
      selectedUser: null
    })
  }

  const deleteUsersHandler = async () => {
    const selectedUsersId = deleteUserModal.isSingleDelete
      ? [deleteUserModal.selectedUser.id]
      : selectedServiceUsersId

    setDeleteUserModal(prevState => ({
      ...prevState,
      isSingleDelete: false,
      isOpen: false
    }))

    const from = moment('2024-01-01').format('YYYY-MM-DD')
    const to = moment().format('YYYY-MM-DD')

    try {
      // QCS has no conversation logs: the export 403s there.
      if (!isQcs) {
        await Promise.all(selectedUsersId.map(userId => exportConversations(botId, userId, from, to)))
      }

      await deleteUsers(botId, selectedUsersId.join(','))

      await getServiceUsers(botId, currentPage)

      if (selectedServiceUsersId.length) {
        clearSelectedServiceUsersId()
      }
    } catch (error) {
      console.error('Error during export or deletion:', error)
    }
  }

  const hasSearch = Boolean(searchingValue.trim())

  const listBody = isLoadingServiceUsers ? (
    <LoaderScreen />
  ) : serviceUsers.length > 0 ? (
    <>
      <ServiceUsersTable
        botId={botId}
        setDeleteUserModal={setDeleteUserModal}
        searchValue={searchingValue}
        page={currentPage}
        rowsPerPage={rowsPerPage}
        onUploadingChange={setIsCarePlanUploading}
      />
      <Pagination
        page={currentPage}
        rowsPerPage={rowsPerPage}
        count={totalElements}
        onChangePage={onChangePageHandler}
        onChangeRowsPerPage={onChangeRowsPerPageHandler}
        hasSelect={true}
        labelRowsPerPage="Show"
      />
    </>
  ) : isQcs ? (
    hasSearch ? (
      <EmptyState
        title="No service users match your search"
        description={`Nothing matches “${searchingValue.trim()}”. Try a different name, or clear the search.`}
      />
    ) : (
      <EmptyState
        title="No service users yet"
        description="Add service users to start tracking the people this organisation supports."
      />
    )
  ) : (
    <NoChartData
      isUserData={true}
      text="You have no Service users yet. Start by pressing the button -  Add new service users"
    />
  )

  return (
    <BotUsageProvider botId={botId} enabled={isQcs}>
      <ServiceUsersContainer>
        {isCarePlanUploading && <LoaderOverlay />}

        {isQcs ? (
          <>
            <PageHeader
              title="Service users"
              count={totalElements}
              actions={
                <Button
                  style={buttonStylesQcs}
                  onClick={() => setIsAddNewServiceUserModalOpen(true)}
                >
                  <ButtonTitleQcs>Add service users</ButtonTitleQcs>
                </Button>
              }
            />
            <FilterBar
              search={
                <Search
                  value={searchingValue}
                  onChange={onChangeHandler}
                  placeholder="Search by service user"
                  inputStyle={filterBarSearchStyle}
                />
              }
              onClear={hasSearch ? () => setSearchingValue('') : undefined}
              total={hasSearch ? undefined : totalElements}
            />
          </>
        ) : (
          <>
            <ServiceUsersHeadlineWrap>
              <Headline title="Service users" />
            </ServiceUsersHeadlineWrap>
            <ServiceUsersControlLine>
              <ServiceUsersSearchWrap>
                <Search
                  value={searchingValue}
                  onChange={onChangeHandler}
                  placeholder="Search by service user"
                  inputStyle={inputSearchStyles}
                />
              </ServiceUsersSearchWrap>
              <ServiceUsersButtonWrap>
                <Button
                  style={buttonStylesCareBrain}
                  onClick={() => setIsAddNewServiceUserModalOpen(true)}
                >
                  <ButtonTitle>Add new service users</ButtonTitle>
                </Button>
              </ServiceUsersButtonWrap>
            </ServiceUsersControlLine>
          </>
        )}

        {listBody}

        {isAddNewServiceUserModalOpen &&
        <AddNewServiceUserModal
          botId={botId}
          isAddNewServiceUserModalOpen={isAddNewServiceUserModalOpen}
          onClose={() => setIsAddNewServiceUserModalOpen(false)}
          setDuplicateAttentionModal={setDuplicateAttentionModal}
        />
        }
        {deleteUserModal.isOpen &&
        <DeleteServiceUserModal
          open={deleteUserModal.isOpen}
          selectedUser={deleteUserModal.selectedUser}
          onClose={onCloseDeleteModalHandler}
          onDelete={deleteUsersHandler}
          isQcs={isQcs}
        />
        }

        {duplicateAttentionModal.isOpen &&
        <DuplicateAttentionModal
          open={duplicateAttentionModal.isOpen}
          userNames={duplicateAttentionModal.duplicateUsers}
          onClose={() => setDuplicateAttentionModal(prevState => ({ ...prevState, isOpen: false}))}
          category='SERVICE_USER'
        />
        }
      </ServiceUsersContainer>
    </BotUsageProvider>
  )
}

const mapStateToProps = (state) => ({
  serviceUsers: state.serviceUsersTab.serviceUsers,
  currentPage: state.serviceUsersTab.currentPage,
  totalElements: state.serviceUsersTab.totalElements,
  isLoadingServiceUsers: state.serviceUsersTab.isLoadingServiceUsers,
  careProfessionals: state.serviceUsersTab.careProfessionals,
  selectedServiceUsersId: state.serviceUsersTab.selectedServiceUsersId,
  rowsPerPage: state.serviceUsersTab.rowsPerPage
})

export default withRouter(connect(mapStateToProps)(ServiceUsers))
